import React from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import StarIcon from "@mui/icons-material/Star";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

export type CaseDetails = {
  id: number;
  publicId: string;
  name: string;
  location: string;
  email: string;
  rankedKeywords: number;
  rankedKeywordsLabel: string;
  totalKw: number;
  successRate: number;
};

type CaseDetailsDialogProps = {
  open: boolean;
  caseData: CaseDetails | null;
  onClose: () => void;
  onViewDetails: (publicId: string) => void;
  onCopyLink: (publicId: string) => void;
};

const MetricCard: React.FC<{
  title: string;
  value: React.ReactNode;
  footer?: React.ReactNode;
  color: "green" | "blue" | "purple";
}> = ({ title, value, footer, color }) => {
  const colors = {
    green: { bg: "#E6F4EA", value: "#137333" },
    blue: { bg: "#E8F0FE", value: "#1967D2" },
    purple: { bg: "#F3E8FD", value: "#8E44AD" },
  };
  const selectedColor = colors[color];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        backgroundColor: selectedColor.bg,
        textAlign: "center",
        height: "100px",
        width: "140px",
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Typography
        variant="h4"
        sx={{ fontWeight: 700, color: selectedColor.value }}
      >
        {value}
      </Typography>
      {footer}
    </Paper>
  );
};

export const CaseDetailsDialog: React.FC<CaseDetailsDialogProps> = ({
  open,
  caseData,
  onClose,
  onViewDetails,
  onCopyLink,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  if (!caseData) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle
        sx={{ pr: 6, display: "flex", alignItems: "center", gap: 1 }}
      >
        <EmojiEventsIcon sx={{ color: "#FFD700" }} />
        <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
          {caseData.name}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ml: 4, mt: -2, mb: 3 }}
        >
          {caseData.location} • {caseData.email}
        </Typography>

        <Grid container spacing={2}>
          {/* eslint-disable-next-line */}
          {/* @ts-ignore */}
          <Grid item xs={12} sm={4}>
            <MetricCard
              title="Ranqueadas"
              value={caseData.rankedKeywords}
              footer={
                <Chip
                  label={caseData.rankedKeywordsLabel}
                  size="small"
                  sx={{ backgroundColor: "#CDE7D6", color: "#137333" }}
                />
              }
              color="green"
            />
          </Grid>
          {/* eslint-disable-next-line */}
          {/* @ts-ignore */}
          <Grid item xs={12} sm={4}>
            <MetricCard
              title="Total KW"
              value={caseData.totalKw}
              color="blue"
            />
          </Grid>
          {/* eslint-disable-next-line */}
          {/* @ts-ignore */}
          <Grid item xs={12} sm={4}>
            <MetricCard
              title="Taxa de Sucesso"
              value={`${caseData.successRate}%`}
              color="purple"
            />
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: "#FFF8E1",
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <StarIcon sx={{ color: "#FBC02D" }} />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              Case de Sucesso Certificado
            </Typography>
            <Typography variant="body2">
              Este perfil alcançou ranqueamento de excelência com 8+
              palavras-chave nas primeiras posições do Google.
            </Typography>
          </Box>
        </Paper>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button
            sx={{ textTransform: "none", width: "100%" }}
            variant="contained"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => onViewDetails(caseData.publicId)}
          >
            Ver Detalhes Completos
          </Button>
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={() => onCopyLink(caseData.publicId)}
            sx={{ textTransform: "none", width: "100%" }}
          >
            Copiar Link
          </Button>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};
